// apps/client/src/hooks/pipelineTracker/usePrefetchSlowTabs.ts
//
// Warm the tabs that take longest to answer while the reader is on the Pipeline tab.
//
// Operations > Overview, Operations > Tasks and the Board pack were the slow ones on stage
// (5 October 2026, demo book): each reads a whole task or deal snapshot server side, and the
// Board pack cannot ask for anything until the Trends report has told it which exports exist.
// Asking for those three answers once the Pipeline tab has its own figures means that by the time
// someone clicks across, the answers are usually already in the session cache (reportCache.ts).
//
// Started only AFTER the Pipeline report has answered, so it never competes with the figures the
// reader is looking at, and only for the default request each tab makes on opening, so it adds
// no question a tab would not ask anyway. A failure here is silent: the tab asks again on opening
// and reports its own error there.

import { useEffect } from 'react';
import { queryClient } from '../../lib/queryClient';
import { GET_MONTHLY_INPUTS_ACTION, OPERATIONS_OVERVIEW_REPORT_ACTION } from './operationsContract';
import {
  PIPELINE_REPORT_ACTION,
  REPORT_CALLABLE,
  TASKS_REPORT_ACTION,
  TAXONOMY_CALLABLE,
  TRENDS_REPORT_ACTION,
} from './reportContract';
import { prefetchTrackerCall, trackerActionKey } from './reportCache';
import { GET_TARGETS_ACTION } from './targetsContract';

/** A beat after the Pipeline figures land, so the page settles before more is asked. */
export const PREFETCH_DELAY_MS = 1500;

export function usePrefetchSlowTabs(companyId: string | undefined): void {
  useEffect(() => {
    if (!companyId) return undefined;
    let started = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cache = queryClient.getQueryCache();
    const pipelineAnswered = () =>
      cache
        .findAll({ queryKey: trackerActionKey(REPORT_CALLABLE, companyId, PIPELINE_REPORT_ACTION) })
        .some((query) => query.state.status === 'success');

    const start = () => {
      if (started) return;
      started = true;
      unsubscribe();
      timer = setTimeout(() => {
        prefetchTrackerCall(REPORT_CALLABLE, { action: OPERATIONS_OVERVIEW_REPORT_ACTION, companyId });
        prefetchTrackerCall(TAXONOMY_CALLABLE, { action: GET_MONTHLY_INPUTS_ACTION, companyId });
        // What the Overview's monthly tiles are graded against.
        prefetchTrackerCall(TAXONOMY_CALLABLE, { action: GET_TARGETS_ACTION, companyId });
        prefetchTrackerCall(REPORT_CALLABLE, { action: TASKS_REPORT_ACTION, companyId });
        // The Board pack's first question, and the one every other Board pack read waits on.
        prefetchTrackerCall(REPORT_CALLABLE, { action: TRENDS_REPORT_ACTION, companyId });
      }, PREFETCH_DELAY_MS);
    };

    const unsubscribe = cache.subscribe(() => {
      if (pipelineAnswered()) start();
    });
    if (pipelineAnswered()) start();

    return () => {
      started = true;
      unsubscribe();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [companyId]);
}
